(ns frontend.view.modals.curtains.opportunity-load
  "The pass curtain and the lost curtains of an opportunity need data of the
   opportunity: its name and its pipeline. The pipeline decides which reasons
   the picker offers. These curtains load the opportunity from the backend
   every time they open, also when the session holds it. A session copy can be
   missing, and the session storage is going away. See \"Load Frontend Data From
   The Backend\" in CLAUDE.md.

   `with-opportunity` loads the opportunity first. The curtain itself is a
   `loaded-view` that gets the loaded entity as `:opportunity`. Until then the
   user sees a loading line, or an error when the load fails. The Confirm
   button stays disabled, and Cancel works. The curtain does not continue with
   partial data."
  (:require
   [common.localization :as loc]
   [frontend.logging :as logging]
   [frontend.updates.entity-scope :as entity-scope]
   [reagent.core :as r]
   [ui.color :as c]
   [ui.components.alert.view :as alert]
   [ui.components.modal.view :as modal]))

(defn load!
  "Loads the opportunity with `entity-scope/load-entity!` and puts the result in
   the atom `load*`: `{:status :loading}`, then `{:status :loaded :opportunity
   entity}` or `{:status :error}`. A load that throws is an error too, for
   example for an id that is not a number."
  [{:keys [load* opportunity-id]}]
  (reset! load* {:status :loading})
  (let [fail! (fn [error]
                (logging/error "Failed to load the opportunity of a curtain" error)
                (reset! load* {:status :error}))]
    (try
      (entity-scope/load-entity!
       {:entity-id opportunity-id
        :on-loaded (fn [opportunity _entities]
                     (reset! load* {:status      :loaded
                                    :opportunity opportunity}))
        :on-error  fail!})
      (catch :default error
        (fail! error)))))

(defn pending-view
  "The body of a curtain that waits for its opportunity: a loading line, or the
   error when the load failed."
  [{:keys [status]}]
  (if (= :error status)
    [alert/danger
     {}
     [:p {:class "m-0"}
      (loc/tr :curtain/opportunity-load-failed)]]
    [:p {:class "m-0"}
     (loc/tr :loading)]))

(defn with-opportunity
  "Loads the opportunity `opportunity-id`, then renders `loaded-view` with
   `args` and the loaded entity as `:opportunity`. `args` are the arguments that
   the modal host gives to the curtain. `header-text` is the title of the
   curtain until the load is done."
  [{:keys [opportunity-id]}]
  (let [load* (r/atom {:status :loading})]
    (load! {:load*          load*
            :opportunity-id opportunity-id})
    (fn [{:keys [args loaded-view header-text]}]
      (let [{:keys [status opportunity] :as load} @load*
            {:keys [shown? topmost? close-modal! props]} args]
        (if (= :loaded status)
          [loaded-view (assoc args :opportunity opportunity)]
          (let [close! (fn []
                         (close-modal!)
                         (when-let [reset-focus! (:reset-focus! props)]
                           (reset-focus!)))]
            [modal/view
             {:visible?      (and shown? topmost?)
              :hide!         close!
              :backdrop      "static"
              :color         ::c/danger
              :close-button? true}
             header-text
             [pending-view load]
             [:<>
              [modal/left-cancel-only close! nil]
              [modal/right-single-button
               ::c/danger
               true
               (fn [])
               (loc/tr :confirm)
               nil]]]))))))
